import { z } from 'zod'

import { LEAD_SOURCES } from '../constants/leads'

const PHONE_REGEX = /^\d{10}$/
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i

export const leadProductSchema = z.object({
  product: z
    .string()
    .trim()
    .min(1, 'Product name is required')
    .refine(
      (val) => /^[a-zA-Z0-9\s.,'()-]+$/.test(val),
      'Product name must be alphanumeric',
    ),
  description: z.string().trim().optional(),
  quantity: z
    .number({ error: 'Quantity must be a number' })
    .int('Quantity must be a whole number')
    .min(1, 'Quantity must be at least 1'),
  value: z
    .number({ error: 'Value must be a number' })
    .min(0, 'Value must be at least 0')
    .optional()
    .nullable(),
})

export const leadFormSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(2, 'Full name must be at least 2 characters')
      .max(120, 'Full name cannot exceed 120 characters')
      .refine(
        (val) => /^[a-zA-Z0-9\s.'-]+$/.test(val),
        'Lead name must be alphanumeric',
      )
      .refine(
        (val) => /[a-zA-Z]/.test(val),
        'Lead name cannot be numeric only',
      ),
    company: z
      .string()
      .trim()
      .min(2, 'Company must be at least 2 characters')
      .max(150, 'Company cannot exceed 150 characters')
      .refine(
        (val) => /^[a-zA-Z0-9\s.,&'()/-]+$/.test(val),
        'Company name must be alphanumeric',
      ),
    phone: z
      .string()
      .optional()
      .transform((value) => (value ? value.replace(/[\s-]/g, '').trim() : ''))
      .refine(
        (value) => !value || PHONE_REGEX.test(value),
        'Phone number must contain only numbers (exactly 10 digits)',
      ),
    email: z
      .string()
      .optional()
      .transform((value) => (value ? value.trim().toLowerCase() : ''))
      .refine(
        (value) => !value || EMAIL_REGEX.test(value),
        'Please enter a valid email address',
      ),
    source: z.enum(
      [LEAD_SOURCES.LINKEDIN, LEAD_SOURCES.FACEBOOK, LEAD_SOURCES.OFFLINE],
      'Invalid lead source selected.',
    ),
    leadWarmth: z.enum(
      ['hot', 'warm', 'cold', 'unqualified'],
      'Invalid lead classification selected.',
    ),
    status: z.enum(['open', 'in_progress', 'won', 'lost']).optional(),
    products: z.array(leadProductSchema).optional().default([]),
  })
  .superRefine((data, ctx) => {
    const hasPhone = Boolean(data.phone && data.phone.length > 0)
    const hasEmail = Boolean(data.email && data.email.length > 0)

    if (!hasPhone && !hasEmail) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please provide either an email or phone number',
        path: ['email'],
      })
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please provide either an email or phone number',
        path: ['phone'],
      })
    }
  })
